import { tr } from '@/lib/i18n-core';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, ChevronRight, Info, ShieldAlert, UserPlus } from 'lucide-react';
import {
  registerPatientSchema,
  type DuplicateCandidate,
  type FamilyRelation,
  type FacilityKind,
  type NextMrnPreview,
  type RegisterPatientInput,
  composeFullName,
  countryProfile,
} from '@his/shared';
import { ApiError, get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useBranchLock, useWorkingBranchId } from '@/lib/branch';
import { activeProfile } from '@/lib/country';
import { useReducedMotion } from '@/lib/motion';
import { FormActions, FormScreen } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { identityForSubmit, missingReasonLabel } from './IdentityDocumentFields';
import { PersonLookupPanel, type PersonMatch } from './PersonLookupPanel';
import type { PatientSummary } from './PatientPicker';
import { linkedPatientDefaults } from './family-link';
import { DuplicateCandidateSheet } from './DuplicateCandidateSheet';
import { PatientFormSections } from './PatientFormSections';
import { PlatformMatches } from './PlatformMatches';
import { ModeSwitch } from './RegistrationSection';
import { ASIDE_CLASS, CompletenessCard, DuplicatesCard, PatientSummaryCard } from './RegistrationAside';
import { RegistrationSuccess, type RegisteredPatient } from './RegistrationSuccess';
import { RegistrationInsurance, useRegistrationInsurance } from './RegistrationInsurance';
import { KEEP_PHOTO, draftPreview, useObjectUrlCleanup, type PhotoDraft } from './patient-photo';
import {
  NO_INSURANCE,
  insuranceProblems,
  runExtra,
  runExtras,
  withRetried,
  type ExtraKind,
  type ExtraResult,
  type InsuranceDraft,
  type RegistrationExtras,
} from './registration-extras';
import { ageSummaryText, categoryLabel, describeRegistered, sexText } from './patient-labels';
import {
  REGISTRATION_MODES,
  ageFacts,
  ageSummary,
  completeness,
  duplicateCheckKey,
  hasChanges,
  missingRequired,
  sectionProgress,
  type RegistrationContext,
  type RegistrationMode,
} from './registration-rules';

const MODE_KEY = 'his.registration.mode';
const DUPLICATES_ANCHOR = 'registration-duplicates';
const PLATFORM_LOOKUP_MIN_LENGTH = 6;
const PLATFORM_LOOKUP_MAX_LENGTH = 24;
const DUPLICATE_CHECK_MAX = { name: 160, phone: 20, cnic: 20 } as const;

function keepLookupEnterOutOfTheForm(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key === 'Enter' && event.target instanceof HTMLInputElement) event.preventDefault();
}

function isMode(value: string | null): value is RegistrationMode {
  return value !== null && (REGISTRATION_MODES as readonly string[]).includes(value);
}

function storedMode(): RegistrationMode | null {
  try {
    const value = window.localStorage.getItem(MODE_KEY);
    return isMode(value) ? value : null;
  } catch {
    return null;
  }
}

function rememberMode(mode: RegistrationMode): void {
  try {
    window.localStorage.setItem(MODE_KEY, mode);
  } catch {
    return;
  }
}

function blankValues(branchId: string | undefined): Partial<RegisterPatientInput> {
  return {
    bloodGroup: 'unknown',
    category: 'general',
    maritalStatus: 'unknown',
    preferredLanguage: 'en',
    acknowledgeDuplicate: false,
    branchId,
    guardianRelation: 'self',
  };
}

const BLANK = blankValues(undefined);

interface SavedPatient {
  id: string;
  mrn: string;
  fullName: string;
  sex: string;
  dob: string | null;
  approximateAgeYears: number | null;
  warnings?: { message: string }[];
}

/**
 * Patient registration (SRS 6.1).
 *
 * Duplicate candidates are shown *while typing*, not only on submit, because a
 * receptionist who sees the existing record before finishing the form will open
 * it rather than create a second MRN for the same person.
 */
export function PatientRegisterPage() {
  const { user, can } = useAuth();
  const [params] = useSearchParams();
  const workingBranchId = useWorkingBranchId();
  const branchLock = useBranchLock();
  const keepsFullRecords = (user?.facilityType ?? 'hospital') === 'hospital';
  const facility = (user?.facilityType ?? 'hospital') as FacilityKind;
  const toast = useToast();
  const reduced = useReducedMotion();
  const [mode, setMode] = useState<RegistrationMode>(() => {
    const asked = params.get('mode');
    return isMode(asked) ? asked : storedMode() ?? 'full';
  });
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[]>([]);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);
  const lastCheck = useRef<string | null>(null);
  const checkRun = useRef(0);
  const [platformMatches, setPlatformMatches] = useState<PersonMatch[]>([]);
  const lastLookup = useRef<string | null>(null);
  const [peeking, setPeeking] = useState<DuplicateCandidate | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [person, setPerson] = useState<PersonMatch | null>(null);
  const [linkedPatient, setLinkedPatient] = useState<PatientSummary | null>(null);
  const [linkedRelation, setLinkedRelation] = useState<FamilyRelation>('mother');
  const [registered, setRegistered] = useState<RegisteredPatient | null>(null);
  const [photo, setPhoto] = useState<PhotoDraft>(KEEP_PHOTO);
  const [insurance, setInsurance] = useState<InsuranceDraft>(NO_INSURANCE);
  const [submittedExtras, setSubmittedExtras] = useState<RegistrationExtras | null>(null);
  const [extraResults, setExtraResults] = useState<ExtraResult[]>([]);
  const insuranceHere = useRegistrationInsurance();
  useObjectUrlCleanup(draftPreview(photo));
  useObjectUrlCleanup(insurance.card?.previewUrl);

  const branches = user?.branches ?? [];
  const effectiveMode: RegistrationMode = keepsFullRecords ? mode : 'full';

  const form = useForm<RegisterPatientInput>({
    resolver: zodResolver(registerPatientSchema),
    defaultValues: blankValues(workingBranchId || undefined),
  });

  const values = useWatch({ control: form.control }) as Partial<RegisterPatientInput>;
  const mrnBranchId = values.branchId || workingBranchId || '';
  const nextMrn = useQuery({
    queryKey: ['patients', 'next-mrn', mrnBranchId],
    queryFn: () => get<NextMrnPreview>(`/patients/next-mrn${qs({ branchId: mrnBranchId || undefined })}`),
    enabled: can('patient.register') && Boolean(mrnBranchId),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const extrasInForm = effectiveMode === 'full';
  const wantedExtras: RegistrationExtras = {
    photo: extrasInForm ? photo : KEEP_PHOTO,
    insurance: extrasInForm && insuranceHere ? insurance : NO_INSURANCE,
  };
  const insuranceIncomplete = insuranceProblems(wantedExtras.insurance).length > 0;
  const chosenBranch = branches.find((branch) => branch.id === values.branchId);
  const profile = chosenBranch?.country ? countryProfile(chosenBranch.country) : activeProfile();
  const facts = ageFacts(values);
  const { isMinor, showGuardianDetails, infant } = facts;
  const guardianRelation = values.guardianRelation;
  const context: RegistrationContext = { profile, facility, keepsFullRecords, mode: effectiveMode, needsBranch: branches.length > 0 };
  const sections = sectionProgress(values, context, facts);
  const progress = Object.fromEntries(sections.map((section) => [section.id, section.done]));
  const missing = missingRequired(values, context, facts);

  // A minor cannot be their own guardian — the moment the age drops below 18
  // (or is entered as such), the relation can no longer stay on Self, so the
  // desk is not left filling in a guardian nobody asked them to name.
  useEffect(() => {
    if (isMinor && guardianRelation === 'self') form.setValue('guardianRelation', 'parent');
  }, [isMinor, guardianRelation, form]);

  // Self means there is no separate guardian, so nothing typed earlier under
  // a different relation should linger and quietly get submitted with it.
  useEffect(() => {
    if (!showGuardianDetails) {
      form.setValue('guardianName', '');
      form.setValue('guardianPhone', '');
    }
  }, [showGuardianDetails, form]);

  useEffect(() => {
    if (infant) return;
    form.setValue('birthWeightGrams', undefined);
    form.setValue('gestationalAgeWeeks', undefined);
    form.setValue('gestationalAgeDays', undefined);
  }, [infant, form]);

  const checkDuplicates = async (force = false) => {
    const current = form.getValues();
    const fullName = composeFullName(profile, current);
    if (fullName.length < 3) return;
    const phone = current.phone?.trim();
    const cnic = current.cnic?.trim();
    const payload = {
      fullName: fullName.slice(0, DUPLICATE_CHECK_MAX.name),
      phone: phone && phone.length <= DUPLICATE_CHECK_MAX.phone ? phone : undefined,
      dob: current.dob || undefined,
      cnic: cnic && cnic.length <= DUPLICATE_CHECK_MAX.cnic ? cnic : undefined,
    };
    const key = duplicateCheckKey(payload);
    if (!force && key === lastCheck.current) return;
    lastCheck.current = key;
    const run = ++checkRun.current;
    setChecking(true);
    try {
      const found = await post<DuplicateCandidate[]>('/patients/duplicate-check', payload);
      if (run === checkRun.current) setDuplicates(found);
    } catch {
      // A failed duplicate check must never block registration.
      if (run === checkRun.current) setDuplicates([]);
    } finally {
      if (run === checkRun.current) {
        setChecking(false);
        setChecked(true);
      }
    }
  };

  const lookUpPlatform = async () => {
    if (person) return;
    const number = form.getValues('cnic')?.trim() ?? '';
    if (number.length < PLATFORM_LOOKUP_MIN_LENGTH || number.length > PLATFORM_LOOKUP_MAX_LENGTH || form.getValues('idType') === 'no_document') {
      lastLookup.current = null;
      setPlatformMatches([]);
      return;
    }
    if (number === lastLookup.current) return;
    lastLookup.current = number;
    try {
      const found = await get<PersonMatch[]>(`/patients/person-lookup${qs({ cnic: number })}`);
      if (lastLookup.current === number) setPlatformMatches(found.slice(0, 3));
    } catch {
      setPlatformMatches([]);
    }
  };

  const associate = useMutation({
    mutationFn: (platformPersonId: string) =>
      post<SavedPatient>('/patients/associate', {
        platformPersonId,
        branchId: form.getValues().branchId,
      }),
    onSuccess: (patient) => {
      toast.success(tr('opd', 'taken_on'), tr('opd', 'mrn_x_no_need_to_register_them_again', { mrn: patient.mrn }));
      setRegistered({ id: patient.id, mrn: patient.mrn, fullName: patient.fullName, details: describeRegistered(patient), quick: false });
    },
    onError: (caught) => setServerError(describeError(caught)),
  });

  const register = useMutation({
    mutationFn: async ({ input, extras }: { input: RegisterPatientInput; extras: RegistrationExtras }) => {
      const patient = await post<SavedPatient>('/patients', input);
      const results = await runExtras(patient.id, extras);
      return { patient, extras, results };
    },
    onSuccess: ({ patient, extras, results }) => {
      for (const warning of patient.warnings ?? []) toast.warning(tr('opd', 'please_check'), warning.message);
      setSubmittedExtras(extras);
      setExtraResults(results);
      setRegistered({ id: patient.id, mrn: patient.mrn, fullName: patient.fullName, details: describeRegistered(patient), quick: effectiveMode === 'quick' });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'conflict') {
        setServerError(error.message);
        void checkDuplicates(true);
        return;
      }
      setServerError(describeError(error));
    },
  });

  const retryExtra = useMutation({
    mutationFn: (kind: ExtraKind) => {
      if (!registered || !submittedExtras) throw new Error(tr('opd', 'extra_retry_unavailable'));
      return runExtra(registered.id, kind, submittedExtras);
    },
    onSuccess: (result) => setExtraResults((current) => withRetried(current, result)),
  });

  const takeIdentityFrom = (match: PersonMatch) => {
    const [firstName, ...rest] = match.fullName.split(' ');
    const dirty = { shouldDirty: true };
    setPerson(match);
    setPlatformMatches([]);
    form.setValue('platformPersonId', match.personId, dirty);
    form.setValue('firstName', firstName, dirty);
    form.setValue('lastName', rest.join(' '), dirty);
    form.setValue('sex', match.sex as RegisterPatientInput['sex'], dirty);
    if (match.dob) {
      form.setValue('dob', match.dob.slice(0, 10), dirty);
      form.setValue('approximateAgeYears', undefined, dirty);
    } else if (match.approximateAgeYears !== null) {
      form.setValue('approximateAgeYears', match.approximateAgeYears, dirty);
    }
    if (match.cnic) form.setValue('cnic', match.cnic, dirty);
    if (match.idType) form.setValue('idType', match.idType as RegisterPatientInput['idType'], dirty);
    if (match.idCountry) form.setValue('idCountry', match.idCountry, dirty);
    if (match.phone) form.setValue('phone', match.phone, dirty);
    void checkDuplicates();
  };

  const forgetIdentity = () => {
    setPerson(null);
    form.setValue('platformPersonId', null);
  };

  const applyFamilyLink = (patient: PatientSummary | null, relation: FamilyRelation) => {
    const previous = linkedPatient;
    setLinkedPatient(patient);
    setLinkedRelation(relation);
    if (!patient) {
      form.setValue('family', undefined);
      return;
    }
    form.setValue('family', { parentPatientId: patient.id, relation }, { shouldDirty: true });
    const defaults = linkedPatientDefaults(form.getValues(), patient, relation, previous);
    if (defaults.phone) form.setValue('phone', defaults.phone);
    if (defaults.guardianRelation) form.setValue('guardianRelation', defaults.guardianRelation);
    if (defaults.guardianName) form.setValue('guardianName', defaults.guardianName);
    if (defaults.guardianPhone) form.setValue('guardianPhone', defaults.guardianPhone);
  };

  const changeMode = (next: RegistrationMode) => {
    setMode(next);
    rememberMode(next);
  };

  const startOver = () => {
    form.reset(blankValues(form.getValues('branchId')));
    setRegistered(null);
    setPhoto(KEEP_PHOTO);
    setInsurance(NO_INSURANCE);
    setSubmittedExtras(null);
    setExtraResults([]);
    void nextMrn.refetch();
    setDuplicates([]);
    setChecked(false);
    lastCheck.current = null;
    lastLookup.current = null;
    setPlatformMatches([]);
    setPerson(null);
    setLinkedPatient(null);
    setServerError(null);
  };

  const jumpToDuplicates = () => {
    document.getElementById(DUPLICATES_ANCHOR)?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  const errors = form.formState.errors;
  const dirty = hasChanges(BLANK, values) || person !== null || linkedPatient !== null || photo.kind !== 'keep' || insurance.enabled;

  if (registered) {
    return (
      <RegistrationSuccess
        patient={registered}
        onRegisterAnother={startOver}
        extras={extraResults}
        retrying={retryExtra.isPending ? (retryExtra.variables ?? null) : null}
        onRetry={(kind) => retryExtra.mutate(kind)}
      />
    );
  }

  const fullName = composeFullName(profile, values);
  const showBranch = !branchLock.locked && (branches.length > 1 || (branches.length > 0 && !values.branchId));
  const idSummary = values.idType === 'no_document' ? (values.idMissingReason ? missingReasonLabel(values.idMissingReason) : null) : values.cnic;

  return (
    <FormScreen
      title={tr('opd', 'register_patient')}
      description={
        keepsFullRecords
          ? tr('opd', 'one_lifetime_record_per_person_on_the_platform')
          : tr('opd', 'a_name_and_a_mobile_number_is_all_this_counter')
      }
      icon={UserPlus}
      wide
      dirty={dirty}
      onSubmit={form.handleSubmit((input) => {
        setServerError(null);
        const prepared = identityForSubmit(input, profile, facility, !isMinor);
        if (prepared.problem) {
          setServerError(prepared.problem);
          return;
        }
        if (insuranceIncomplete) {
          setServerError(tr('opd', 'insurance_incomplete'));
          return;
        }
        register.mutate({ input: prepared.values, extras: wantedExtras });
      })}
      aside={
        <div className={ASIDE_CLASS}>
          <PatientSummaryCard
            name={fullName}
            sex={sexText(values.sex) ?? ''}
            age={ageSummaryText(ageSummary(values.dob, values.approximateAgeYears))}
            phone={values.phone}
            idNumber={idSummary}
            branch={branches.length > 1 ? chosenBranch?.name ?? null : undefined}
            category={values.category && values.category !== 'general' ? categoryLabel(values.category) : null}
            mrn={null}
            likelyMrn={nextMrn.data?.mrn ?? null}
            photoSrc={extrasInForm && photo.kind === 'new' ? photo.previewUrl : null}
            mode={keepsFullRecords ? effectiveMode : null}
            isMinor={isMinor}
            infant={infant}
            footer={keepsFullRecords && effectiveMode === 'quick' ? tr('opd', 'quick_mode_footer') : undefined}
          />
          {effectiveMode === 'full' && <CompletenessCard sections={sections} percent={completeness(sections)} keepsFullRecords={keepsFullRecords} />}
          <DuplicatesCard
            id={DUPLICATES_ANCHOR}
            candidates={duplicates}
            checking={checking}
            checked={checked}
            onOpen={setPeeking}
            acknowledgement={
              <Controller
                control={form.control}
                name="acknowledgeDuplicate"
                render={({ field }) => (
                  <Checkbox
                    ref={field.ref}
                    checked={Boolean(field.value)}
                    onCheckedChange={field.onChange}
                    label={tr('opd', 'this_is_a_different_person_register_anyway')}
                  />
                )}
              />
            }
          />
        </div>
      }
    >
      {keepsFullRecords && (
        <div className="space-y-2">
          <ModeSwitch mode={effectiveMode} onChange={changeMode} />
          <p key={effectiveMode} className="flex items-start gap-2 px-1 text-xs text-muted-foreground motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300">
            <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {effectiveMode === 'quick' ? tr('opd', 'quick_mode_note') : tr('opd', 'full_mode_note')}
          </p>
        </div>
      )}

      {showBranch && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          </span>
          <Field label={tr('opd', 'registering_at')} required error={errors.branchId?.message} className="min-w-[12rem] flex-1">
            <Select {...form.register('branchId')}>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}

      {duplicates.length > 0 && (
        <button
          type="button"
          onClick={jumpToDuplicates}
          className="flex items-center gap-3 rounded-xl border border-warning/50 bg-warning/10 px-4 py-3 text-start text-sm font-medium text-warning focus-ring motion-safe:animate-in motion-safe:fade-in-0 lg:hidden"
        >
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">{tr('opd', 'duplicates_found_count', { count: duplicates.length })}</span>
          <ChevronRight className="h-4 w-4 shrink-0 rtl:rotate-180" aria-hidden />
        </button>
      )}

      <div onKeyDown={keepLookupEnterOutOfTheForm}>
        <PersonLookupPanel
          selected={person}
          onSelect={takeIdentityFrom}
          onClear={forgetIdentity}
          onAssociate={(match) => associate.mutate(match.personId)}
          associating={associate.isPending}
        />
      </div>

      <PatientFormSections
        form={form}
        purpose="register"
        profile={profile}
        facility={facility}
        keepsFullRecords={keepsFullRecords}
        mode={effectiveMode}
        facts={facts}
        progress={progress}
        autoFocusName
        onLookupField={() => void checkDuplicates()}
        onIdentityCommit={() => {
          void checkDuplicates();
          void lookUpPlatform();
        }}
        identityExtra={
          platformMatches.length > 0 && !person ? (
            <PlatformMatches
              matches={platformMatches}
              onUse={takeIdentityFrom}
              onAssociate={(match) => associate.mutate(match.personId)}
              associating={associate.isPending}
            />
          ) : null
        }
        family={{ patient: linkedPatient, relation: linkedRelation, onChange: applyFamilyLink }}
        photo={{ value: photo, onChange: setPhoto, currentKey: null, name: fullName }}
        insuranceInForm={insuranceHere}
        extraSections={insuranceHere ? <RegistrationInsurance draft={insurance} onChange={setInsurance} /> : undefined}
      />

      {serverError && <Alert variant="destructive">{serverError}</Alert>}

      <FormActions
        submitLabel={effectiveMode === 'quick' ? tr('opd', 'register_quick_submit') : tr('opd', 'register_and_allocate_mrn')}
        loading={register.isPending}
        incomplete={missing.length > 0 || insuranceIncomplete}
      />

      {peeking && <DuplicateCandidateSheet candidate={peeking} onClose={() => setPeeking(null)} />}
    </FormScreen>
  );
}
